import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useLocation } from 'wouter';
import Config from '../../../config';
import showMessage from '../Toastr/ToastService';
import { IVehicleData, IVehicleDriver, TVehicleStatus } from '../Vehicles/types';
import { selectedVehiclesAtom } from '../../store/recoil/vehicles';
import { vehicleDataByIdSelector } from '../../store/recoil/vehiclesData';
import { toggleSelectVehicle } from '../../Services/VehicleService';
import { isOnlinePanelOpenAtom } from '../OnlinePanel/OnlinePanelState';
import {
  autofocusAtom,
  isRidePlayerOpenAtom,
  vehicleFocusAtom,
} from '../OnlineMapNew/OnlineMapState';
import {
  getNavigatorStatusColor,
  getNavigatorStatusColorStroke,
  getPath,
  getPathStroke,
} from './NavigatorIconService';
import { isMinimalizedCarsIconsAtom } from './NavigatorState';
import useAnalytics from '../../hooks/useAnalytics';

interface IVehicleStatusClass {
  [key: string]: string;
}

interface IVehicleTypeClass {
  [key: string]: string;
}

interface INavigatorVehicleProps {
  id: number;
  name: string;
  licenceNumber: string;
  driver: IVehicleDriver;
  status: TVehicleStatus;
  selected: boolean;
  vehicleTypeId: number;
  customerId?: number;
  groupId?: number;
}

export default function VehicleNew(props: INavigatorVehicleProps) {
  const setSelectedVehicles = useSetRecoilState(selectedVehiclesAtom);
  const vehicleData = useRecoilValue<IVehicleData | null>(vehicleDataByIdSelector(props.id));
  const selectedVehicles = useRecoilValue(selectedVehiclesAtom);
  const [isOnlinePanelOpen, setIsOnlinePanelOpen] = useRecoilState<number | false>(
    isOnlinePanelOpenAtom
  );
  const [isRidePlayerOpen, setIsRidePlayerOpen] = useRecoilState<number | boolean>(
    isRidePlayerOpenAtom
  );
  const { formatMessage: f } = useIntl();
  const setVehicleFocus = useSetRecoilState<boolean>(vehicleFocusAtom);
  const setAutofocus = useSetRecoilState<boolean>(autofocusAtom);
  const isMinimalizedCarsIcons = useRecoilValue<boolean>(isMinimalizedCarsIconsAtom);
  const [location, navigate] = useLocation();
  const { trackEvent } = useAnalytics();

  const getStatusClassName = (status: TVehicleStatus) =>
    (Config.Vehicle.statusClass as IVehicleStatusClass)[status] || null;

  const getVehicleTypeClassName = (vehicleTypeId: number) =>
    (Config.Vehicle.typeClass as IVehicleTypeClass)[vehicleTypeId] || null;

  const handleSelectVehicle = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>
  ): boolean => {
    event.preventDefault();
    const vehicleId = event.currentTarget.getAttribute('data-vehicle-id');
    trackEvent(['navigator', 'click', 'Select vehicle in navigator']);

    if (vehicleId && isRidePlayerOpen && !selectedVehicles.includes(Number(vehicleId))) {
      showMessage(
        'navigator.toastr.selectVehicleDisabled.title',
        'navigator.toastr.selectVehicleDisabled.message',
        'warning',
        4000
      );
      return false;
    }
    if (
      (vehicleId && !isRidePlayerOpen) ||
      (Number(vehicleId) === Number(isOnlinePanelOpen) && isRidePlayerOpen)
    ) {
      if (
        Config.selectedVehiclesLimit.enabled &&
        selectedVehicles.length > Config.selectedVehiclesLimit.limit
      ) {
        showMessage(
          'navigator.toastr.selectedVehicleLimit.title',
          'navigator.toastr.selectedVehicleLimit.message',
          'warning',
          null,
          { limit: Config.selectedVehiclesLimit.limit }
        );
        return false;
      }
      // Store selected vehicle to recoil
      setSelectedVehicles((state: number[]) => toggleSelectVehicle(state, Number(vehicleId)));

      if (!props.selected) {
        setIsOnlinePanelOpen(false);
      }
    }
    return true;
  };

  const handleSelectVehicleWithOnlinePanel = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>
  ): boolean => {
    event.preventDefault();
    trackEvent(['navigator', 'click', 'Select vehicle in navigator with online panel']);
    const vehicleId = event.currentTarget.getAttribute('data-vehicle-id');

    if (isRidePlayerOpen && String(isRidePlayerOpen) === String(vehicleId)) {
      setIsRidePlayerOpen(false);
      setIsOnlinePanelOpen(false);
      return false;
    }
    const openOnlinePanel = event.currentTarget.getAttribute('data-open-online-panel');

    if (vehicleId && isRidePlayerOpen && !selectedVehicles.includes(Number(vehicleId))) {
      showMessage(
        'navigator.toastr.selectVehicleDisabled.title',
        'navigator.toastr.selectVehicleDisabled.message',
        'warning',
        4000
      );
      return false;
    }
    if (
      (vehicleId && !isRidePlayerOpen) ||
      (Number(vehicleId) === Number(isOnlinePanelOpen) && isRidePlayerOpen)
    ) {
      if (
        Config.selectedVehiclesLimit.enabled &&
        selectedVehicles.length > Config.selectedVehiclesLimit.limit
      ) {
        showMessage(
          'navigator.toastr.selectedVehicleLimit.title',
          'navigator.toastr.selectedVehicleLimit.message',
          'warning',
          null,
          { limit: Config.selectedVehiclesLimit.limit }
        );
        return false;
      }

      // Open online panel
      if (openOnlinePanel && !selectedVehicles.includes(Number(vehicleId))) {
        // Store selected vehicle to recoil
        setSelectedVehicles((state: number[]) => toggleSelectVehicle(state, Number(vehicleId)));
        if (vehicleData?.position) {
          setIsOnlinePanelOpen(Number(vehicleId));
          setAutofocus(false);
          setVehicleFocus(true);
        }
      } else if (
        selectedVehicles.includes(Number(vehicleId)) &&
        typeof isOnlinePanelOpen === 'number' &&
        isOnlinePanelOpen === Number(vehicleId)
      ) {
        setIsOnlinePanelOpen(false);
      } else if (
        selectedVehicles.includes(Number(vehicleId)) &&
        typeof isOnlinePanelOpen === 'number' &&
        isOnlinePanelOpen !== Number(vehicleId)
      ) {
        if (vehicleData?.position) {
          setIsOnlinePanelOpen(Number(vehicleId));
          setAutofocus(false);
          setVehicleFocus(true);
        }
      } else if (vehicleData?.position) {
        setIsOnlinePanelOpen(Number(vehicleId));
        setAutofocus(false);
        setVehicleFocus(true);
      }
    }
    return true;
  };

  let classList = `cmd-icons customer-vehicle-checkbox-${props.customerId} ${
    props.selected ? 'cmd-icon-check' : ''
  }`;
  if (props.groupId) {
    classList = `cmd-icons group-vehicle-checkbox-${props.groupId} ${
      props.selected ? 'cmd-icon-check' : ''
    }`;
  }

  let statusTitle = '';
  if (getStatusClassName(props.status)) {
    statusTitle = f({
      id: `navigator.status.${getStatusClassName(props.status)}`,
    });
    const vehicleClassName = getVehicleTypeClassName(props.vehicleTypeId);
    if (vehicleClassName) {
      const localeString = `navigator.car.${vehicleClassName.replace('icon-', '')}`;
      statusTitle += ` ${f({
        id: localeString,
      })}`;
    }
  }

  const svg = (
    <svg
      width="24px"
      height="24px"
      viewBox="0 0 24 24"
      version="1.1"
      data-cy={`navigator-icon-${props.vehicleTypeId}-${props.status}`}
      className="b-navigator-listing-item-vehicle-icon"
    >
      <path
        id="path"
        d={props.id === Config.mucha ? getPathStroke(999) : getPathStroke(props.vehicleTypeId)}
        fill={`#${getNavigatorStatusColorStroke(
          props.status,
          Number(props.id) === Number(isOnlinePanelOpen) && location.includes('maps')
        )}`}
        fillOpacity="1"
        stroke="none"
      />
      <path
        id="path"
        d={props.id === Config.mucha ? getPath(999) : getPath(props.vehicleTypeId)}
        fill={`#${getNavigatorStatusColor(
          props.status,
          Number(props.id) === Number(isOnlinePanelOpen) && location.includes('maps')
        )}`}
        fillOpacity="1"
        stroke="none"
      />
    </svg>
  );

  const getMinimalizedStatusIcon = (vehicleStatus: TVehicleStatus): string => {
    switch (vehicleStatus) {
      case 'DRIVING':
        return 'green';
      case 'STOP':
        return 'red';
      case 'PRIVATE':
        return 'dark-gray';
      case 'NO_GSM':
        return 'yellow';
      case 'NO_GPS':
        return 'blue';
      default:
        return 'gray';
    }
  };

  if (isMinimalizedCarsIcons) {
    return (
      <div className="b-navigator-listing" style={{ height: 'auto' }}>
        <div
          className={`b-navigator-listing-item b-navigator-listing-item--collapsed ${
            props.selected && Number(props.id) === Number(isOnlinePanelOpen)
              ? 'b-navigator-listing-item--active '
              : ''
          } row no-gutters align-items-center`}
          title={`ID: ${props.id} - ${props.name} ${
            props.driver && props.driver.name ? `- ${props.driver.name}` : ''
          }`}
        >
          <div
            className="col b-navigator-listing-item-vehicle"
            data-vehicle-id={props.id}
            data-open-online-panel="1"
            onClick={handleSelectVehicle}
            data-cy={`navigator-vehicle-${props.id}`}
          >
            <div className="row no-gutters align-items-center">
              <div className="col-auto pr-4">
                <div className="e-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={props.selected}
                      onChange={handleSelectVehicle}
                    />
                    <span />
                  </label>
                </div>
              </div>
              <div
                className="col"
                id={String(props.id)}
                title={`ID: ${props.id} - ${props.name} ${
                  props.driver && props.driver.name ? `- ${props.driver.name}` : ''
                }`}
              >
                <div
                  className="text-truncate b-navigator-listing-item-vehicle-title"
                  style={{ width: '150px' }}
                  data-cy="navigator-carName"
                >
                  <strong> {props.name}</strong>
                </div>
                <div
                  className="text-truncate b-navigator-listing-item-vehicle-driver"
                  data-cy={`navigator-driverName-${props.driver.id}`}
                >
                  {props.driver.name}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{ cursor: 'pointer' }}
            className="col-auto text-center b-navigator-listing-item-spz"
            title={`ID: ${props.id} - ${props.name} ${
              props.driver && props.driver.name ? `- ${props.driver.name}` : ''
            }`}
            onClick={handleSelectVehicleWithOnlinePanel}
            data-vehicle-id={props.id}
            data-open-online-panel="1"
            data-cy={`navigator-vehicle-online-panel-${props.id}`}
          >
            <div className="d-flex align-items-center justify-content-end">
              <span
                className="text-truncate d-inline-block mr-1"
                style={{ width: '60px' }}
                data-cy="navigator-licenceNumber"
              >
                {props.licenceNumber}
              </span>
              <span
                className={`b-navigator-listing-item-vehicle-state b-navigator-listing-item-vehicle-state--${
                  Number(props.id) === Number(isOnlinePanelOpen) && location.includes('maps')
                    ? 'solid'
                    : 'full'
                }-${getMinimalizedStatusIcon(props.status)}`}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="b-navigator-listing" style={{ height: 'auto' }}>
      <div
        className={`b-navigator-listing-item ${
          props.selected && Number(props.id) === Number(isOnlinePanelOpen)
            ? 'b-navigator-listing-item--active'
            : ''
        } row no-gutters align-items-center`}
        title={`ID: ${props.id} - ${props.name} ${
          props.driver && props.driver.name ? `- ${props.driver.name}` : ''
        }`}
      >
        <div
          className="col b-navigator-listing-item-vehicle"
          onClick={handleSelectVehicle}
          data-vehicle-id={props.id}
          data-open-online-panel="1"
          data-cy={`navigator-vehicle-${props.id}`}
        >
          <div className="row no-gutters align-items-center">
            <div className="col-auto pr-4">
              <div className="e-checkbox">
                <label htmlFor={`navigator-checkbox-${props.id}`}>
                  <input
                    id={`navigator-checkbox-${props.id}`}
                    type="checkbox"
                    data-vehicle-id={props.id}
                    data-open-online-panel="1"
                    checked={props.selected}
                    onChange={handleSelectVehicle}
                  />
                  <span />
                </label>
              </div>
            </div>
            <div
              id={String(props.id)}
              className="col"
              title={`ID: ${props.id} - ${props.name} ${
                props.driver && props.driver.name ? `- ${props.driver.name}` : ''
              }`}
            >
              <div
                className="text-truncate b-navigator-listing-item-vehicle-title"
                data-cy="navigator-carName"
                style={{ width: '150px' }}
                title={`ID: ${props.id} - ${props.name} ${
                  props.driver && props.driver.name ? `- ${props.driver.name}` : ''
                }`}
              >
                <strong>{props.name}</strong>
              </div>
              {props.driver.name && (
                <div
                  className="text-truncate b-navigator-listing-item-vehicle-driver"
                  title={`ID: ${props.id} - ${props.name} ${
                    props.driver && props.driver.name ? `- ${props.driver.name}` : ''
                  }`}
                  data-cy={`navigator-driverName-${props.driver.id}`}
                  style={{ width: '150px' }}
                >
                  {props.driver.name}
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          style={{ cursor: 'pointer' }}
          title={`ID: ${props.id} - ${props.name} ${
            props.driver && props.driver.name ? `- ${props.driver.name}` : ''
          }`}
          className="col-auto text-center b-navigator-listing-item-spz"
          onClick={handleSelectVehicleWithOnlinePanel}
          data-vehicle-id={props.id}
          data-open-online-panel="1"
          data-cy={`navigator-vehicle-online-panel-${props.id}`}
        >
          <div
            title={`ID: ${props.id} - ${props.name} ${
              props.driver && props.driver.name ? `- ${props.driver.name}` : ''
            }`}
            style={{ textAlign: 'center' }}
          >
            {/* 24 x24 */}
            {props.status !== 'UNKNOWN' && svg}
            {props.status === 'UNKNOWN' && (
              <svg
                width="24px"
                height="24px"
                viewBox="0 0 24 24"
                version="1.1"
                className="b-navigator-listing-item-vehicle-icon"
              >
                <g
                  id="map/marker/vehicle/24px/no-position"
                  stroke="none"
                  strokeWidth="1"
                  fill="none"
                  fillRule="evenodd"
                >
                  <path
                    d="M6.71791878,4.00000016 L13.0751001,4.00379451 L13.3027307,4.01103217 C14.6877052,4.08673723 15.9637353,4.7527601 16.8072768,5.81042277 L17.4085608,6.56433438 L20.1473909,7.09306289 L20.407996,7.15349114 C21.3819909,7.40701473 22.2166632,7.94631722 22.831089,8.65605929 C23.5493561,9.48575126 23.9697852,10.5465236 23.983489,11.6845273 L24,13.0426752 L23.9943359,13.2808613 C23.9356913,14.4983478 23.4150698,15.6062504 22.5776851,16.4260053 C21.9985078,16.9929889 21.266035,17.420747 20.4414726,17.6385947 L20.3858933,17.6526858 L20.3475777,17.6846523 C19.3777833,18.484775 18.1498867,18.9561608 16.8478861,18.997211 L16.7129396,18.9999596 C15.6094469,19.0039653 14.5886802,18.7099907 13.7191137,18.2019932 L9.04701801,18.187319 C8.34288029,18.4437563 7.58660828,18.5826722 6.7998977,18.5826722 C4.91386652,18.5826722 3.20277638,17.7842736 1.96556191,16.4822614 C0.7548453,15.2081349 0,13.4496141 0,11.5036938 C0,9.55777354 0.754845298,7.79925277 1.96556193,6.52512618 C3.00697134,5.42917412 4.38413192,4.69004307 5.91876231,4.48353962 C5.95548875,4.41418437 6.00168723,4.34812347 6.05776986,4.28770551 C6.22819061,4.10411082 6.46741884,3.99985065 6.71791878,4.00000016 Z"
                    id="Combined-Shape"
                    fill="#737373"
                  />
                  <path
                    d="M10.6316129,6.45093945 L13.037011,6.45237514 L13.1967243,6.45745332 C13.8853117,6.50130809 14.5240652,6.82717404 14.9457396,7.35588451 L16.100594,8.80388264 L19.6581939,9.49067389 L19.840261,9.53289101 C20.3177403,9.66255851 20.7291923,9.9268975 21.0323044,10.2770316 C21.3733056,10.6709324 21.5771892,11.1734178 21.5837097,11.714896 L21.599675,13.0281592 L21.5959338,13.1854835 C21.5621077,13.7575079 21.3094587,14.2740805 20.9160837,14.6591735 C20.4959757,15.0704368 19.9153875,15.331721 19.2696661,15.349771 C19.2042866,15.4296864 19.134057,15.5065823 19.0597178,15.5804051 C18.4419357,16.1938959 17.5923441,16.5436574 16.6926893,16.5514026 C15.8372568,16.5545173 15.0528822,16.2545517 14.4525384,15.7556876 L14.4397827,15.7445239 L11.3709344,15.745986 C11.8101585,15.1766898 12.1628561,14.5304721 12.4079513,13.8295121 L14.8233671,13.8295453 C14.6013783,13.2270149 14.7634186,12.5518836 15.2356681,12.1117183 C15.7079177,11.6715528 16.4000171,11.5505736 16.9966698,11.8038945 C17.5933225,12.0572154 17.9803661,12.6363666 17.9814767,13.2775065 L17.9785302,13.3672714 L17.9705826,13.45674 L18.4877235,13.450746 C18.8546011,13.4461139 19.1541424,13.1759915 19.1948419,12.8307879 L19.1995134,12.7430329 L19.1900573,11.9332719 C19.1865628,11.6340416 18.9882282,11.3737798 18.7027836,11.2792816 L18.6146447,11.2560319 L14.8257881,10.5017794 L13.412516,8.67448937 C13.2959437,8.52376736 13.1226326,8.42622641 12.9329112,8.40143353 L12.8368696,8.39519002 L12.0784775,8.39439262 C11.7751539,7.78213357 11.3850446,7.22506807 10.925319,6.74126489 C10.8303381,6.64130942 10.7323856,6.54448116 10.6316129,6.45093945 Z M6.7998977,6.87331836 C8.01490588,6.87331836 9.1148893,7.39159079 9.91112071,8.22952393 C10.7073521,9.06745708 11.1998315,10.2250509 11.1998315,11.5036938 C11.1998315,12.7823367 10.7073521,13.9399306 9.91112071,14.7778637 C9.1148893,15.6157968 8.01490588,16.1340692 6.7998977,16.1340692 C5.58488953,16.1340692 4.48490608,15.6157968 3.68867466,14.7778637 C2.89244326,13.9399306 2.39996389,12.7823367 2.39996389,11.5036938 C2.39996389,10.2250509 2.89244326,9.06745708 3.68867466,8.22952393 C4.48490608,7.39159079 5.58488953,6.87331836 6.7998977,6.87331836 Z M16.355819,11.9523747 C15.9988269,11.9526914 15.6565839,12.0929847 15.4043794,12.3423914 C15.152175,12.5917983 15.0106683,12.9298886 15.0109864,13.2822861 C15.0333003,14.0231811 15.649726,14.6115319 16.4006062,14.6086766 C16.7516052,14.6055387 17.0866064,14.4632597 17.3302147,14.2137969 C17.573823,13.9643341 17.7055706,13.6286474 17.6958064,13.2822861 C17.6964808,12.5503169 17.0973241,11.9556656 16.355819,11.9523747 Z M9.31603075,9.96344311 L5.3371005,14.1520968 C5.76689308,14.4155698 6.26664741,14.5664942 6.7998977,14.5664942 C7.60357496,14.5664942 8.33116818,14.2236786 8.85784208,13.6694208 C9.38451598,13.1151629 9.71027056,12.3494628 9.71027056,11.5036938 C9.71027056,10.9421673 9.56667943,10.4159346 9.31603075,9.96344311 Z M6.7998977,8.44089339 C5.99622042,8.44089339 5.2686272,8.783709 4.7419533,9.33796686 C4.2152794,9.89222474 3.88952482,10.6579248 3.88952482,11.5036938 C3.88952482,12.0648725 4.03293809,12.5908009 4.28329885,13.0431034 L8.26349419,8.85578106 C7.83352194,8.59200496 7.33347856,8.44089339 6.7998977,8.44089339 Z M12.5900165,8.92930541 C12.7887044,8.92930541 12.9769764,9.00895307 13.1118983,9.14686078 L13.1747147,9.22057062 L13.5408768,9.7155424 C13.6033334,9.79997016 13.6369308,9.90131308 13.6369308,10.0052791 C13.6369308,10.2545605 13.4478098,10.4609338 13.2013402,10.496396 L13.1258107,10.5017794 L12.7294353,10.5014883 C12.6516834,9.95301172 12.5110926,9.42613699 12.3156176,8.92923561 L12.5900165,8.92930541 Z"
                    id="Combined-Shape"
                    fill="#C8C8C8"
                  />
                </g>
              </svg>
            )}
            <div
              className="text-truncate"
              data-cy="navigator-licenceNumber"
              style={{ width: '60px' }}
            >
              {props.licenceNumber}
              <span className="b-navigator-listing-item-vehicle-state b-navigator-listing-item-vehicle-state--full-red" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
